import {
  formatDate as formatDateLib,
  fromNow as fromNowLib,
  parseDate,
} from "@/lib/dateTime";

const allCapsWords = ["tv"];

function applyAllCapsWords(text: string): string {
  const words = [...allCapsWords]
    .filter(Boolean)
    .sort((a, b) => b.length - a.length);

  return words.reduce((result, word) => {
    const escaped = word.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(`(^|\\s)(${escaped})(?=\\s|$)`, "gi");
    return result.replace(
      pattern,
      (_match, prefix: string) => `${prefix}${word.toUpperCase()}`,
    );
  }, text);
}

export const titleize = function (value: string | undefined | null): string {
  const titleized =
    value
      ?.replace(/_/g, " ")
      .replace(/(?:^|\s|-)\S/g, (x) => x.toUpperCase()) || "";
  return applyAllCapsWords(titleized);
};

export const abbreviateNumber = function (number: number): string {
  const formatter = new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
  });
  return formatter.format(number);
};

export const audienceAvailability = function (value: string): string {
  const xref: Record<string, string> = {
    archived: "Archived",
    internal: "Internal Only",
    external: "External",
    none: "None",
  };
  return xref[value] || value;
};

export const formatTV = function (segment: any) {
  const selections = [];
  const order = [
    "month",
    "tv_categories",
    "viewers",
    "network",
    "callsign",
    "program",
    "time_zone",
    "program_start_local",
    "program_end_local",
  ];
  for (const key of order) {
    if (segment[key] !== undefined) {
      switch (key) {
        case "month":
          selections.push(formatDateLib(segment.month, "MMM YYYY"));
          break;
        case "tv_categories":
          selections.push(segment.tv_categories.join(", "));
          break;
        case "viewers":
          selections.push(`${titleize(segment.viewers)} Viewers`);
          break;
        case "network":
          if (segment.network && segment.callsign) {
            selections.push(`${segment.network}/${segment.callsign}`);
            break;
          }
          selections.push(segment.network);
          break;
        case "program":
          selections.push(segment.program);
          break;
        case "time_zone":
          selections.push(`${segment.time_zone}`);
          break;
        case "program_start_local":
          selections.push(`Start: ${segment.program_start_local}`);
          break;
        case "program_end_local":
          selections.push(`End: ${segment.program_end_local}`);
          break;
      }
    }
  }
  return selections.join(" · ");
};
export const formatAudienceComposition = function (audienceComposition: any) {
  if (!audienceComposition) {
    return "";
  }
  const audiences = audienceComposition.audiences.join(" + ");
  const mediaHabits = audienceComposition.media_habits
    .map((segment: any) => formatTV(segment))
    .join(" + ");
  const parts = [];
  if (audiences) parts.push(audiences);
  if (mediaHabits) parts.push(mediaHabits);

  return parts.join(" + ");
};
export const audienceType = function (value: string): string {
  if (value === "Premium") return "Premier";
  return value;
};

export const formatDate = function (
  date: string | Date | null | undefined,
): string {
  return date ? formatDateLib(date, "MM/DD/YYYY") : "";
};

export const formatUtcDate = function (
  date: string | Date | null | undefined,
): string {
  return date ? formatDateLib(date, "MM/DD/YYYY", { utc: true }) : "";
};

export const formatLongDate = function (
  date: string | Date | null | undefined,
): string {
  return date ? formatDateLib(date, "MMMM D, YYYY") : "";
};

export const formatDateTime = function (
  date: string | Date | null | undefined,
): string {
  return date ? formatDateLib(date, "MM/DD/YYYY h:mm A") : "";
};

export const formatDateRange = function (dates: string[]): string {
  if (!dates || dates.length !== 2) return "";
  const startDate = parseDate(dates[0]);
  const endDate = parseDate(dates[1]);

  if (
    startDate.getFullYear() === endDate.getFullYear() &&
    startDate.getMonth() === endDate.getMonth()
  ) {
    return `${formatDateLib(startDate, "MMM D")}-${formatDateLib(endDate, "D, YYYY")}`;
  }

  if (startDate.getFullYear() === endDate.getFullYear()) {
    return `${formatDateLib(startDate, "MMM D")}-${formatDateLib(endDate, "MMM D, YYYY")}`;
  }

  return `${formatDateLib(startDate, "MMM D, YYYY")} - ${formatDateLib(endDate, "MMM D, YYYY")}`;
};

export const fromNow = function (
  date: string | Date | null | undefined,
): string {
  return date ? fromNowLib(date) : "";
};

export type FiltersType = {
  pluralize: (word: string, count?: number) => string;
  titleize: typeof titleize;
  abbreviateNumber: typeof abbreviateNumber;
  audienceAvailability: typeof audienceAvailability;
  audienceType: typeof audienceType;
  formatDate: typeof formatDate;
  formatUtcDate: typeof formatUtcDate;
  formatLongDate: typeof formatLongDate;
  formatDateTime: typeof formatDateTime;
  formatDateRange: typeof formatDateRange;
  fromNow: typeof fromNow;
  formatAudienceComposition: typeof formatAudienceComposition;
  formatTV: typeof formatTV;
};
